import { useMonkAppState, useMonkState } from '@monkvision/common';
import { useCallback, useMemo } from 'react';
import { useMonkApi } from '@monkvision/network';
import { useMonitoring } from '@monkvision/monitoring';
import { HagertyAdditionalData, HagertyInspectionStatus } from './types';

export function useUpdateHagertyAdditionalData() {
  const { config, authToken, inspectionId } = useMonkAppState({
    requireInspection: true,
  });
  const { updateAdditionalData } = useMonkApi({
    authToken,
    apiDomain: config.apiDomain,
    thumbnailDomain: config.thumbnailDomain,
  });
  const {
    state: { inspections },
  } = useMonkState();
  const inspection = useMemo(() => {
    const currentInspection = inspections.find((i) => i.id === inspectionId);
    if (!currentInspection) {
      return undefined;
    }
    return currentInspection;
  }, [inspections, inspectionId]);
  const { handleError } = useMonitoring();

  const updateHagertyInspection = useCallback(
    async (additionalData: HagertyAdditionalData) => {
      try {
        await updateAdditionalData({
          id: inspectionId,
          callback: () => additionalData,
        });
      } catch (err) {
        handleError(err);
      }
    },
    [updateAdditionalData, handleError, inspectionId],
  );

  const handlePictureTaken = useCallback(() => {
    if (!inspection) {
      throw new Error('Unable to find current inspection in the local Monk State');
    }
    if (inspection.images.length === 0) {
      const now = new Date().toISOString();
      const additionalData: HagertyAdditionalData = {
        customer_status: HagertyInspectionStatus.ONGOING_CAPTURE,
        last_update_date: now,
        inspection_start: now,
      };
      return updateHagertyInspection(additionalData);
    }
    return Promise.resolve();
  }, [inspection, updateHagertyInspection]);

  const handleInspectionCompleted = useCallback(async () => {
    const now = new Date().toISOString();
    const additionalData: HagertyAdditionalData = {
      customer_status: HagertyInspectionStatus.READY_FOR_REVIEW,
      last_update_date: now,
      inspection_end: now,
    };
    return updateHagertyInspection(additionalData);
  }, [updateHagertyInspection]);

  return { handlePictureTaken, handleInspectionCompleted };
}
